// Works with both Tiptap JSON content (changelogs) and HTML strings (docs).

function isBlankText(node: unknown): boolean {
	const { type, text } = (node ?? {}) as { type?: string; text?: unknown }
	return type === "text" && (typeof text !== "string" || text.trim() === "")
}

export function isContentEmpty(content: unknown): boolean {
	if (!content) return true

	if (typeof content === "string") {
		const trimmed = content.trim()
		return (
			trimmed === "" ||
			trimmed === "<p></p>" ||
			trimmed === "<p><br></p>" ||
			trimmed === "<br>"
		)
	}

	if (typeof content === "object") {
		const contentArray = (content as { content?: unknown[] }).content
		if (!Array.isArray(contentArray) || contentArray.length === 0) return true

		return contentArray.every((node) => {
			if (typeof node !== "object" || node === null) return true
			const nodeObj = node as { type?: string; content?: unknown[] }
			if (nodeObj.type === "paragraph") {
				return (nodeObj.content ?? []).every(isBlankText)
			}
			return false
		})
	}

	return true
}

export interface VersionState {
	title: string
	notes?: unknown // Changelogs (Tiptap JSON)
	content?: unknown // Docs (HTML string)
	imageUrl: string | null
}

export interface ShouldCreateVersionOptions {
	currentTitle: string
	currentContent: unknown
	currentImageUrl: string | null
	lastSavedVersion: VersionState | null
}

// Every real change is worth a snapshot; the version mutator folds a run of
// saves by one author into a single row, so this no longer throttles by time or
// change size. Skipping saves here used to leave the newest text unrecoverable.
export function shouldCreateAutoVersion({
	currentTitle,
	currentContent,
	currentImageUrl,
	lastSavedVersion,
}: ShouldCreateVersionOptions): boolean {
	if (!currentTitle.trim() && isContentEmpty(currentContent)) {
		return false
	}

	if (lastSavedVersion === null) {
		return true
	}

	const lastContent = lastSavedVersion.notes ?? lastSavedVersion.content

	return (
		lastSavedVersion.title !== currentTitle ||
		lastSavedVersion.imageUrl !== currentImageUrl ||
		JSON.stringify(lastContent) !== JSON.stringify(currentContent)
	)
}
